import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useFirebaseAuth } from "@/hooks/useFirebaseAuth";
import { ATTENTION_QUEUE_FETCH_LIMIT, ATTENTION_QUEUE_LIMIT, type AttentionQueueData } from "@/lib/attention-queue";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import { queryKeys } from "@/lib/query-keys";
import type { Conversation } from "@/types/chat";

export interface AttentionQueue {
  items: Conversation[];
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Conversations waiting for a person: the bot handed off or the customer asked
 * for one, an order waits for admin approval, or the AI couldn't answer (daily
 * budget, LINE quota). A staff reply or a decision on the order clears it.
 *
 * Shared by the sidebar badge, the home page and the empty inbox. One
 * `LiveQuerySync` in the app shell keeps it current from the event stream.
 */
export function useAttentionQueue(): AttentionQueue {
  const { isAuthenticated } = useFirebaseAuth();
  const query = useQuery({
    queryKey: queryKeys.conversations.attention,
    queryFn: async (): Promise<AttentionQueueData> => {
      const data = edenData(
        await eden.v1.conversations.get({ query: { needsAttention: true, limit: ATTENTION_QUEUE_FETCH_LIMIT } }),
      );
      return { items: Array.isArray(data.items) ? data.items : [] };
    },
    enabled: isAuthenticated,
  });
  const loaded = query.data?.items ?? [];
  return {
    items: loaded.slice(0, ATTENTION_QUEUE_LIMIT),
    hasMore: loaded.length > ATTENTION_QUEUE_LIMIT,
    // Pending, not loading: while auth is still settling the query is disabled
    // and not fetching, and "nothing waiting" must not flash in that gap.
    isLoading: query.isPending,
    isError: query.isError,
  };
}

export interface AttentionBadge {
  /** Short count, e.g. "3" or "5+". */
  text: string;
  /** What a screen reader says in its place. */
  label: string;
}

/** The inbox badge: how many chats wait for a person, or null when none do. */
export function useAttentionBadge(): AttentionBadge | null {
  const { t } = useTranslation();
  const { items, hasMore } = useAttentionQueue();
  const count = items.length;
  if (count === 0) return null;
  if (hasMore) return { text: `${count}+`, label: t("common:nav.attentionBadgeMore", { count }) };
  return { text: String(count), label: t("common:nav.attentionBadge", { count }) };
}
